import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';
import { ApiResponse } from '../../models/auth/auth-session.model';
import { AuthService } from '../auth/auth.service';
import {
  CompanyBranding,
  CompanyBillingPreferences,
  CompanyGeneralInfo,
  UpdateCompanyBillingPreferencesPayload,
  UpdateCompanyBrandingPayload,
  UpdateCompanyGeneralInfoPayload
} from '../../models/company/company.model';

@Injectable({
  providedIn: 'root'
})
export class CompanyService {
  private readonly baseUrl = environment.apiBaseUrl;
  private readonly generalInfoStoragePrefix = 'signly.company.general-info.';
  private readonly generalInfoSubject = new BehaviorSubject<CompanyGeneralInfo | null>(null);
  private generalInfoRequest$?: Observable<CompanyGeneralInfo>;

  constructor(private http: HttpClient, private authService: AuthService) {
    this.hydrateGeneralInfoCache();
  }

  getGeneralInfo(options?: { forceRefresh?: boolean }): Observable<CompanyGeneralInfo> {
    const forceRefresh = !!options?.forceRefresh;
    const cached = this.getCachedGeneralInfo();

    if (!forceRefresh && cached) {
      return of(cached);
    }

    if (!forceRefresh && this.generalInfoRequest$) {
      return this.generalInfoRequest$;
    }

    const request$ = this.executeWithHeaders(headers =>
      this.http
        .get<ApiResponse<CompanyGeneralInfo>>(`${this.baseUrl}/company`, { headers })
        .pipe(
          map(res => res.data),
          tap(data => this.setGeneralInfoCache(data)),
          catchError(err => this.handleError(err))
        )
    ).pipe(
      finalize(() => {
        this.generalInfoRequest$ = undefined;
      }),
      shareReplay(1)
    );

    this.generalInfoRequest$ = request$;
    return request$;
  }

  updateGeneralInfo(payload: UpdateCompanyGeneralInfoPayload): Observable<CompanyGeneralInfo> {
    return this.executeWithHeaders(
      headers =>
        this.http
          .put<ApiResponse<CompanyGeneralInfo>>(`${this.baseUrl}/company`, payload, { headers })
          .pipe(
            map(res => res.data),
            tap(data => this.setGeneralInfoCache(data)),
            catchError(err => this.handleError(err))
          ),
      true
    );
  }

  getCachedGeneralInfo(): CompanyGeneralInfo | null {
    const cached = this.generalInfoSubject.value;
    if (cached) {
      return cached;
    }

    const stored = this.readGeneralInfoFromStorage();
    if (stored) {
      this.generalInfoSubject.next(stored);
      return stored;
    }

    return null;
  }

  generalInfoChanges(): Observable<CompanyGeneralInfo | null> {
    return this.generalInfoSubject.asObservable();
  }

  getBranding(): Observable<CompanyBranding> {
    return this.executeWithHeaders(headers =>
      this.http
        .get<ApiResponse<CompanyBranding>>(`${this.baseUrl}/company/branding`, { headers })
        .pipe(
          map(res => res.data),
          catchError(err => this.handleError(err))
        )
    );
  }

  updateBranding(payload: UpdateCompanyBrandingPayload): Observable<CompanyBranding> {
    return this.executeWithHeaders(
      headers =>
        this.http
          .put<ApiResponse<CompanyBranding>>(`${this.baseUrl}/company/branding`, payload, { headers })
          .pipe(
            map(res => res.data),
            catchError(err => this.handleError(err))
          ),
      true
    );
  }

  getBillingPreferences(): Observable<CompanyBillingPreferences> {
    return this.executeWithHeaders(headers =>
      this.http
        .get<ApiResponse<CompanyBillingPreferences>>(`${this.baseUrl}/company/billing-preferences`, { headers })
        .pipe(
          map(res => res.data),
          catchError(err => this.handleError(err))
        )
    );
  }

  updateBillingPreferences(payload: UpdateCompanyBillingPreferencesPayload): Observable<CompanyBillingPreferences> {
    return this.executeWithHeaders(
      headers =>
        this.http
          .put<ApiResponse<CompanyBillingPreferences>>(`${this.baseUrl}/company/billing-preferences`, payload, {
            headers
          })
          .pipe(
            map(res => res.data),
            catchError(err => this.handleError(err))
          ),
      true
    );
  }

  private executeWithHeaders<T>(fn: (headers: HttpHeaders) => Observable<T>, includeJson = false): Observable<T> {
    try {
      const headers = this.buildHeaders(includeJson);
      return fn(headers);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo establecer la sesión.';
      return throwError(() => new Error(message));
    }
  }

  private buildHeaders(includeJson = false): HttpHeaders {
    const token = this.authService.getAccessToken();
    if (!token) {
      throw new Error('No existe una sesión activa para ejecutar esta operación.');
    }
    let headers = new HttpHeaders().set('X-Auth-Signly', `Bearer ${token}`);
    if (includeJson) {
      headers = headers.set('Content-Type', 'application/json');
    }
    return headers;
  }

  private handleError(error: any) {
    const message = error?.error?.message || error?.message || 'Ocurrió un error al procesar la solicitud.';
    return throwError(() => new Error(message));
  }

  private setGeneralInfoCache(data: CompanyGeneralInfo | null): void {
    this.generalInfoSubject.next(data);
    this.generalInfoRequest$ = undefined;

    if (!data) {
      this.clearGeneralInfoStorage();
      return;
    }

    this.writeGeneralInfoToStorage(data);
  }

  private hydrateGeneralInfoCache(): void {
    const stored = this.readGeneralInfoFromStorage();
    if (stored) {
      this.generalInfoSubject.next(stored);
    }
  }

  private readGeneralInfoFromStorage(): CompanyGeneralInfo | null {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const raw = window.localStorage.getItem(this.generalInfoStorageKey());
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as CompanyGeneralInfo) : null;
    } catch {
      return null;
    }
  }

  private writeGeneralInfoToStorage(data: CompanyGeneralInfo): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(this.generalInfoStorageKey(), JSON.stringify(data));
    } catch {
      // no-op
    }
  }

  private clearGeneralInfoStorage(): void {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.removeItem(this.generalInfoStorageKey());
    } catch {
      // no-op
    }
  }

  private generalInfoStorageKey(): string {
    const tenantId = this.authService.getSession()?.user?.tenantId || 'anonymous';
    return `${this.generalInfoStoragePrefix}${tenantId}`;
  }
}
